import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import ActivityLogger from '../utils/activityLogger';
import ActivityTimeline from './ActivityTimeline';
import AIRelanceGenerator from './AIRelanceGenerator';
import SearchableSelect from './SearchableSelect';
import TruckTypeSelect from './TruckTypeSelect';
import Pagination from './Pagination';
import './css/BaobabDesignSystem.css';
import './css/CompactForms.css';

const OperationsModule = ({ createData, onDataUsed }) => {
  const { user } = useAuth();
  const [operations, setOperations] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [merchandiseTypes, setMerchandiseTypes] = useState([]);
  const [customMerchandiseInput, setCustomMerchandiseInput] = useState('');
  const [truckTypes, setTruckTypes] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  
  // Phase 2D - New state variables
  const [transporters, setTransporters] = useState([]);
  const [users, setUsers] = useState([]); // Tous les utilisateurs pour le champ Opérateur
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showTransporterModal, setShowTransporterModal] = useState(false);
  const [showPaymentTermsModal, setShowPaymentTermsModal] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);
  const [emailForm, setEmailForm] = useState({
    recipient_email: '',
    subject: '',
    message: ''
  });
  const [statusForm, setStatusForm] = useState({
    status: '',
    comment: ''
  });
  const [transporterForm, setTransporterForm] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    specialties: [],
    notes: ''
  });
  const [paymentTermsForm, setPaymentTermsForm] = useState({
    name: '',
    description: '',
    payment_delay_days: 30,
    discount_percentage: 0,
    discount_days: 0,
    is_default: false
  });
  
  // Phase 2 - Module Opérations: States pour le modal de clôture
  const [showClotureModal, setShowClotureModal] = useState(false);
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [clotureData, setClotureData] = useState({
    date_depart_reelle: '',
    heure_depart_reelle: '',
    date_arrivee_reelle: '',
    heure_arrivee_reelle: '',
    has_incident: false,
    incident_types: [],
    incident_cr: ''
  });
  
  // Vue et filtres
  const [viewMode, setViewMode] = useState('grid');
  const [activeTab, setActiveTab] = useState('operations'); // 'operations' or 'history'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterOpportunity, setFilterOpportunity] = useState(''); // 'all', 'with', 'without'
  const [sortField, setSortField] = useState('date_creation');
  const [sortDirection, setSortDirection] = useState('desc');
  const [exportLoading, setExportLoading] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // New states for view and history modals
  const [showViewModal, setShowViewModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  // AI Relance functionality
  const [showAIRelanceModal, setShowAIRelanceModal] = useState(false);

  // États pour les documents associés à une opération
  const [associatedDocuments, setAssociatedDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [activeViewTab, setActiveViewTab] = useState('details'); // 'details', 'documents', 'history'
  
  // États pour l'affichage de document dans modal
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [documentPreviewContent, setDocumentPreviewContent] = useState('');
  const [previewDocumentTitle, setPreviewDocumentTitle] = useState('');

  // Batch selection states
  const [selectedOperations, setSelectedOperations] = useState([]);
  const [showBatchActionsModal, setShowBatchActionsModal] = useState(false);

  // Dropdown menu state
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.baobab-dropdown') && !event.target.closest('.group\\/menu')) {
        setActiveDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  const [formData, setFormData] = useState({
    id: '',
    numero_operation: '',
    opportunite_id: '',
    description: '',
    type_marchandise: '',
    mode_transport: 'routier',
    nombre_conteneurs: 0,
    type_conteneur: '',
    poids_kg: 0,
    volume_m3: 0,
    valeur_estimee_xof: 0,
    priorite: 'Standard',
    lieu_collecte: '',
    lieu_transit: '',
    lieu_livraison: '',
    port_depart: '',
    port_arrivee: '',
    client_id: '',
    client_name: '',
    equipements_necessaires: [],
    date_prevue_execution: '',
    notes: '',
    statut: 'planifiée',
    // Nouveaux champs ajoutés
    poids_marchandise_kg: 0,
    ptra_vehicule_tonnes: 0,
    operateur_id: '',
    operateur_name: '',
    chauffeur_id: '',
    chauffeur_name: '',
    date_chargement_prevue: '',
    date_dechargement_prevue: '',
    // Phase 1 - Module Facturation: Pénalités d'immobilisation
    facturer_penalites_immobilisation: false,
    // Phase 2 - Module Opérations: Suivi des incidents et retards
    date_depart_reelle: '',
    date_arrivee_reelle: '',
    has_incident: false,
    incident_types: [],
    incident_cr: '',
    // Phase 2D - New fields
    transporter_id: '',
    transporter_name: '',
    transporter_contact: '',
    transporter_phone: '',
    transporter_email: '',
    payment_terms_id: '',
    payment_terms_name: '',
    payment_delay_days: null,
    status_details: ''
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/export/operations/csv`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'operations_export.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Erreur lors de l\'export CSV');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'export CSV');
    } finally {
      setExportLoading(false);
    }
  };

  const equipmentOptions = [
    'Camion porteur 19T',
    'Camion porteur 26T',
    'Camion porteur 32T',
    'Camion tracteur 40T',
    'Camion tracteur 44T',
    'Semi-remorque plateau',
    'Semi-remorque bâchée',
    'Semi-remorque citerne',
    'Semi-remorque frigorifique',
    'Camion grue mobile',
    'Chariot élévateur',
    'Grue portuaire',
    'Remorque porte-conteneurs',
    'Camion hayon élévateur',
    'Services de douane',
    'Manutention portuaire',
    'Stockage temporaire',
    'Emballage spécialisé',
    'Escorte convoi',
    'Transport frigorifique',
    'Services de dédouanement',
    'Assurance transport'
  ];

  // Combiner les équipements classiques avec les types de camions de l'API
  const getAllEquipmentOptions = () => {
    const truckTypeOptions = truckTypes.map(truck => truck.type);
    return [...equipmentOptions, ...truckTypeOptions];
  };

  const containerTypes = [
    "20' Standard",
    "40' Standard", 
    "40' High Cube",
    "45' High Cube",
    "20' Open Top",
    "40' Open Top",
    "20' Flat Rack",
    "40' Flat Rack",
    "20' Réfrigéré",
    "40' Réfrigéré",
    "20' DRY",
    "40' DRY"
  ];

  const statusOptions = [
    'planifiée',
    'en_cours',
    'terminée',
    'annulée',
    'archivée'
  ];

  // Phase 2D - Enhanced status options
  const advancedStatusOptions = [
    { value: 'brouillon', label: 'Brouillon', color: 'bg-gray-500' },
    { value: 'devis_envoyé', label: 'Devis envoyé', color: 'bg-blue-500' },
    { value: 'devis_accepté', label: 'Devis accepté', color: 'bg-green-500' },
    { value: 'devis_refusé', label: 'Devis refusé', color: 'bg-red-500' },
    { value: 'bon_commande_envoyé', label: 'BC envoyé', color: 'bg-purple-500' },
    { value: 'bon_commande_accepté', label: 'BC accepté', color: 'bg-indigo-500' },
    { value: 'facture_envoyée', label: 'Facture envoyée', color: 'bg-orange-500' },
    { value: 'facture_payée', label: 'Facture payée', color: 'bg-green-600' },
    { value: 'annulé', label: 'Annulé', color: 'bg-red-600' }
  ];

  const transportSpecialties = ['maritime', 'routier', 'aérien', 'ferroviaire'];

  const priorityOptions = [
    'Faible',
    'Standard',
    'Élevée',
    'Urgent',
    'Critique'
  ];

  const transportModes = [
    'routier',
    'maritime',
    'aérien',
    'ferroviaire'
  ];

  useEffect(() => {
    fetchOperations();
    fetchOpportunities();
    fetchMerchandiseTypes();
    fetchTruckTypes();
    fetchTransporters();
    fetchUsers(); // Charger les utilisateurs opérateurs
    fetchPaymentTerms();
    fetchIncidentTypes(); // Phase 2 - Charger les types d'incidents
  }, []);

  // Handle creation data from opportunities module
  useEffect(() => {
    if (createData) {
      setFormData(prev => ({
        ...prev,
        opportunite_id: createData.opportunite_id || '',
        description: createData.opportunite_nom ? `Opération pour: ${createData.opportunite_nom}` : '',
        client: createData.client || ''
      }));
      setShowCreateModal(true);
      // Mark data as used
      if (onDataUsed) {
        onDataUsed();
      }
    }
  }, [createData, onDataUsed]);

  const fetchTruckTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/truck-types`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTruckTypes(data.truck_types || []);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des types de camions:', error);
    }
  };

  // Phase 2 - Fonction pour charger les types d'incidents
  const fetchIncidentTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/incident-types`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIncidentTypes(data.incident_types || []);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des types d\'incidents:', error);
    }
  };

  const fetchOperations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/operations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOperations(data);
      }
    } catch (error) {
      console.error('Error fetching operations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOpportunities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/opportunities`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOpportunities(data);
      }
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    }
  };

  const fetchMerchandiseTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/merchandise-types`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMerchandiseTypes(data);
      }
    } catch (error) {
      console.error('Error fetching merchandise types:', error);
    }
  };

  // Phase 2D - New fetch functions
  const fetchTransporters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/transporters`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTransporters(data);
      }
    } catch (error) {
      console.error('Error fetching transporters:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filtrer uniquement les utilisateurs avec profil "operateur"
        const operateurs = data.filter(user => user.role === 'operateur');
        setUsers(operateurs);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchPaymentTerms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/payment-terms`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPaymentTerms(data);
      }
    } catch (error) {
      console.error('Error fetching payment terms:', error);
    }
  };

  const fetchEmailHistory = async (operationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/documents/${operationId}/email-history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmailHistory(data);
      }
    } catch (error) {
      console.error('Error fetching email history:', error);
    }
  };

  const fetchStatusHistory = async (operationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/documents/${operationId}/status-history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStatusHistory(data);
      }
    } catch (error) {
      console.error('Error fetching status history:', error);
    }
  };

  const handleCreateOperation = async (e) => {
    e.preventDefault();
    
    try {
      // Validation des champs obligatoires
      const missingFields = [];
      
      // N° Opération sera généré automatiquement s'il n'est pas fourni
      if (!formData.description || formData.description.trim() === '') missingFields.push("Description");
      if (!formData.type_marchandise) missingFields.push("Type de marchandise");
      if (!formData.lieu_collecte || formData.lieu_collecte.trim() === '') missingFields.push("Lieu de collecte");
      if (!formData.lieu_livraison || formData.lieu_livraison.trim() === '') missingFields.push("Lieu de livraison");
      if (!formData.client_name || formData.client_name.trim() === '') missingFields.push("Client");
      
      if (missingFields.length > 0) {
        alert(`Veuillez remplir les champs obligatoires suivants: ${missingFields.join(', ')}`);
        return;
      }

      // Préparer les données pour l'API
      const payload = {
        ...formData,
        nombre_conteneurs: parseInt(formData.nombre_conteneurs) || 0,
        poids_kg: parseFloat(formData.poids_kg) || 0,
        volume_m3: parseFloat(formData.volume_m3) || 0,
        valeur_estimee_xof: parseFloat(formData.valeur_estimee_xof) || 0,
        equipements_necessaires: formData.equipements_necessaires || [],
        date_prevue_execution: formData.date_prevue_execution ? new Date(formData.date_prevue_execution).toISOString() : null,
        payment_delay_days: formData.payment_delay_days ? parseInt(formData.payment_delay_days) : null
      };

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/operations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const newOperation = await response.json();
        
        // Log the operation creation activity
        await ActivityLogger.operationCreated(user, newOperation);
        
        await fetchOperations();
        setShowCreateModal(false);
        resetForm();
        alert('Opération créée avec succès !');
      } else {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        alert(`Erreur lors de la création de l'opération: ${errorData.detail || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Error creating operation:', error);
      alert('Erreur lors de la création de l\'opération');
    }
  };

  const handleUpdateOperation = async (e) => {
    e.preventDefault();
    
    try {
      // Préparer seulement les champs nécessaires pour la mise à jour
      const updatePayload = {
        numero_operation: formData.numero_operation,
        opportunite_id: formData.opportunite_id || null,
        description: formData.description,
        type_marchandise: formData.type_marchandise,
        mode_transport: formData.mode_transport,
        nombre_conteneurs: parseInt(formData.nombre_conteneurs) || 0,
        type_conteneur: formData.type_conteneur,
        poids_kg: parseFloat(formData.poids_kg) || 0,
        volume_m3: parseFloat(formData.volume_m3) || 0,
        valeur_estimee_xof: parseFloat(formData.valeur_estimee_xof) || 0,
        priorite: formData.priorite,
        lieu_collecte: formData.lieu_collecte,
        lieu_transit: formData.lieu_transit,
        lieu_livraison: formData.lieu_livraison,
        port_depart: formData.port_depart,
        port_arrivee: formData.port_arrivee,
        client_name: formData.client_name,
        equipements_necessaires: formData.equipements_necessaires || [],
        date_prevue_execution: formData.date_prevue_execution ? new Date(formData.date_prevue_execution).toISOString() : null,
        notes: formData.notes
      };

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/operations/${formData.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatePayload)
      });
      
      if (response.ok) {
        const updatedOperation = await response.json();
        
        // Log the operation update activity
        await ActivityLogger.operationUpdated(user, updatedOperation, ['general_update']);
        
        await fetchOperations();
        setShowEditModal(false);
        resetForm();
        alert('Opération mise à jour avec succès !');
      } else {
        const errorText = await response.text();
        console.error('Update error:', errorText);
        alert('Erreur lors de la mise à jour de l\'opération: ' + errorText);
      }
    } catch (error) {
      console.error('Error updating operation:', error);
      alert('Erreur lors de la mise à jour de l\'opération: ' + error.message);
    }
  };

  const handleDeleteOperation = (operation) => {
    setSelectedOperation(operation);
    setShowDeleteModal(true);
  };

  const confirmDeleteOperation = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/operations/${selectedOperation.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        // Log the delete activity before fetching operations
        await ActivityLogger.operationUpdated(user, selectedOperation, ['suppression']);
        
        await fetchOperations();
        setShowDeleteModal(false);
        setSelectedOperation(null);
        alert('Opération supprimée avec succès !');
      } else {
        alert('Erreur lors de la suppression de l\'opération');
      }
    } catch (error) {
      console.error('Error deleting operation:', error);
      alert('Erreur lors de la suppression de l\'opération');
    }
  };

  const handleEditOperation = (operation) => {
    setFormData({
      id: operation.id,
      numero_operation: operation.numero_operation,
      opportunite_id: operation.opportunite_id,
      description: operation.description,
      type_marchandise: operation.type_marchandise,
      mode_transport: operation.mode_transport,
      nombre_conteneurs: operation.nombre_conteneurs,
      type_conteneur: operation.type_conteneur || '',
      poids_kg: operation.poids_kg,
      volume_m3: operation.volume_m3,
      valeur_estimee_xof: operation.valeur_estimee_xof,
      priorite: operation.priorite,
      lieu_collecte: operation.lieu_collecte,
      lieu_transit: operation.lieu_transit || '',
      lieu_livraison: operation.lieu_livraison,
      port_depart: operation.port_depart || '',
      port_arrivee: operation.port_arrivee || '',
      client_name: operation.client_name,
      equipements_necessaires: operation.equipements_necessaires || [],
      date_prevue_execution: operation.date_prevue_execution ? operation.date_prevue_execution.split('T')[0] : '',
      notes: operation.notes || '',
      statut: operation.statut
    });
    setIsEditing(true);
    setShowEditModal(true);
  };

  const handleDuplicateOperation = async (operation) => {
    try {
      const token = localStorage.getItem('token');
      
      // Créer un nouveau numéro d'opération
      const newNumero = `OP-${Date.now()}`;
      const duplicatedData = {
        numero_operation: newNumero,
        opportunite_id: operation.opportunite_id,
        description: `${operation.description} (Copie)`,
        type_marchandise: operation.type_marchandise,
        mode_transport: operation.mode_transport,
        nombre_conteneurs: operation.nombre_conteneurs,
        type_conteneur: operation.type_conteneur || '',
        poids_kg: operation.poids_kg,
        volume_m3: operation.volume_m3,
        valeur_estimee_xof: operation.valeur_estimee_xof,
        priorite: operation.priorite,
        lieu_collecte: operation.lieu_collecte,
        lieu_transit: operation.lieu_transit || '',
        lieu_livraison: operation.lieu_livraison,
        port_depart: operation.port_depart || '',
        port_arrivee: operation.port_arrivee || '',
        client_name: operation.client_name,
        equipements_necessaires: operation.equipements_necessaires || [],
        date_prevue_execution: operation.date_prevue_execution,
        notes: operation.notes || '',
        statut: 'en_cours' // Remettre en cours
      };
      
      const response = await fetch(`${API_BASE_URL}/api/operations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(duplicatedData)
      });
      
      if (response.ok) {
        const newOperation = await response.json();
        
        // Log the duplication activity
        try {
          await ActivityLogger.operationCreated(user, newOperation, ['duplication']);
        } catch (logError) {
          console.warn('Failed to log operation duplication:', logError);
        }
        
        await fetchOperations();
        alert('Opération dupliquée avec succès !');
        
        // Ouvrir directement en édition pour permettre les modifications rapides
        setTimeout(() => {
          handleEditOperation(newOperation);
        }, 500);
      } else {
        console.error('Error duplicating operation');
        alert('Erreur lors de la duplication');
      }
    } catch (error) {
      console.error('Error duplicating operation:', error);
      alert('Erreur lors de la duplication');
    }
  };

  const handleArchiveOperation = async (operation) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/operations/${operation.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ statut: 'archivée' })
      });
      
      if (response.ok) {
        const updatedOperation = await response.json();
        
        // Log the archive activity
        await ActivityLogger.operationUpdated(user, updatedOperation, ['statut']);
        
        await fetchOperations();
        alert('Opération archivée avec succès !');
      } else {
        alert('Erreur lors de l\'archivage de l\'opération');
      }
    } catch (error) {
      console.error('Error archiving operation:', error);
      alert('Erreur lors de l\'archivage de l\'opération');
    }
  };

  const handleViewOperation = (operation) => {
    setSelectedOperation(operation);
    setActiveViewTab('details'); // Reset to details tab
    loadAssociatedDocuments(operation.id);
    setShowViewModal(true);
  };

  // Fonction pour charger les documents associés à une opération
  const loadAssociatedDocuments = async (operationId) => {
    setLoadingDocuments(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const allDocuments = await response.json();
        // Filtrer les documents associés à cette opération
        const operationDocuments = allDocuments.filter(doc => doc.operation_id === operationId);
        setAssociatedDocuments(operationDocuments);
      } else {
        console.error('Erreur lors du chargement des documents');
        setAssociatedDocuments([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
      setAssociatedDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Fonction pour ouvrir un document dans un modal
  const handleViewDocument = async (document) => {
    try {
      setLoadingDocuments(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${document.id}/html-preview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const htmlContent = await response.text();
        setDocumentPreviewContent(htmlContent);
        setPreviewDocumentTitle(`${document.type.toUpperCase()} - ${document.reference}`);
        setShowDocumentPreview(true);
      } else {
        console.error('Erreur lors du chargement du document:', response.status);
        alert('Erreur lors du chargement du document');
      }
    } catch (error) {
      console.error('Erreur lors de l\'ouverture du document:', error);
      alert('Erreur lors de l\'ouverture du document');
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleViewOperationHistory = (operation) => {
    setSelectedOperation(operation);
    setShowHistoryModal(true);
  };

  const handleAIRelance = (operation) => {
    setSelectedOperation(operation);
    setShowAIRelanceModal(true);
  };

  const handleStatusChange = async (operation, newStatus) => {
    try {
      // Phase 2 - Si le statut passe à "terminée", déclencher le modal de clôture
      if (newStatus === 'terminée') {
        // Stocker l'opération sélectionnée et ouvrir le modal de clôture
        setSelectedOperation(operation);
        setClotureData({
          date_depart_reelle: '',
          heure_depart_reelle: '',
          date_arrivee_reelle: '',
          heure_arrivee_reelle: '',
          has_incident: false,
          incident_types: [],
          incident_cr: ''
        });
        setShowClotureModal(true);
        return; // Sortir sans mettre à jour le statut maintenant
      }

      const token = localStorage.getItem('token');
      const oldStatus = operation.statut;
      
      const response = await fetch(`${API_BASE_URL}/api/operations/${operation.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ statut: newStatus })
      });
      
      if (response.ok) {
        const updatedOperation = await response.json();
        
        // Log the status change activity
        await ActivityLogger.operationUpdated(user, updatedOperation, ['statut']);
        
        await fetchOperations();
        alert(`Statut de l'opération changé vers "${newStatus}" avec succès !`);
      } else {
        alert('Erreur lors du changement de statut');
      }
    } catch (error) {
      console.error('Error changing status:', error);
      alert('Erreur lors du changement de statut');
    }
  };

  // Phase 2 - Fonction pour gérer la clôture d'opération
  const handleClotureOperation = async (e) => {
    e.preventDefault();
    
    // Validation des champs obligatoires
    if (!clotureData.date_depart_reelle || !clotureData.heure_depart_reelle ||
        !clotureData.date_arrivee_reelle || !clotureData.heure_arrivee_reelle) {
      alert('Les dates et heures de départ et d\'arrivée sont obligatoires pour clôturer l\'opération.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Combiner date et heure en datetime
      const dateDepart = new Date(`${clotureData.date_depart_reelle}T${clotureData.heure_depart_reelle}:00`);
      const dateArrivee = new Date(`${clotureData.date_arrivee_reelle}T${clotureData.heure_arrivee_reelle}:00`);
      
      const updateData = {
        statut: 'terminée',
        date_depart_reelle: dateDepart.toISOString(),
        date_arrivee_reelle: dateArrivee.toISOString(),
        has_incident: clotureData.has_incident,
        incident_types: clotureData.incident_types,
        incident_cr: clotureData.incident_cr || null
      };
      
      const response = await fetch(`${API_BASE_URL}/api/operations/${selectedOperation.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        const updatedOperation = await response.json();
        
        // Log the operation closure activity
        await ActivityLogger.operationUpdated(user, updatedOperation, ['statut', 'cloture']);
        
        await fetchOperations();
        setShowClotureModal(false);
        setSelectedOperation(null);
        
        alert('Opération clôturée avec succès !');
      } else {
        const errorData = await response.json();
        alert(`Erreur lors de la clôture : ${errorData.detail || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Error closing operation:', error);
      alert('Erreur lors de la clôture de l\'opération');
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      numero_operation: '',
      opportunite_id: '',
      description: '',
      type_marchandise: '',
      mode_transport: 'routier',
      nombre_conteneurs: 0,
      type_conteneur: '',
      poids_kg: 0,
      volume_m3: 0,
      valeur_estimee_xof: 0,
      priorite: 'Standard',
      lieu_collecte: '',
      lieu_transit: '',
      lieu_livraison: '',
      port_depart: '',
      port_arrivee: '',
      client_name: '',
      equipements_necessaires: [],
      date_prevue_execution: '',
      notes: '',
      statut: 'planifiée'
    });
    setIsEditing(false);
  };

  // Batch selection functions
  const handleSelectOperation = (operation) => {
    setSelectedOperations(prev => {
      if (prev.find(op => op.id === operation.id)) {
        return prev.filter(op => op.id !== operation.id);
      } else {
        return [...prev, operation];
      }
    });
  };

  const handleSelectAllOperations = (checked) => {
    if (checked) {
      setSelectedOperations(filteredAndSortedOperations());
    } else {
      setSelectedOperations([]);
    }
  };

  const handleBatchDelete = async () => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedOperations.length} opération(s) ?`)) {
      try {
        const deletePromises = selectedOperations.map(operation => 
          fetch(`${API_BASE_URL}/api/operations/${operation.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          })
        );
        
        await Promise.all(deletePromises);
        alert(`${selectedOperations.length} opération(s) supprimée(s) avec succès !`);
        setSelectedOperations([]);
        setShowBatchActionsModal(false);
        fetchOperations();
      } catch (error) {
        console.error('Error deleting operations:', error);
        alert('Erreur lors de la suppression des opérations');
      }
    }
  };

  const handleBatchArchive = async () => {
    if (window.confirm(`Êtes-vous sûr de vouloir archiver ${selectedOperations.length} opération(s) ?`)) {
      try {
        const archivePromises = selectedOperations.map(operation => 
          fetch(`${API_BASE_URL}/api/operations/${operation.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ statut: 'archivée' })
          })
        );
        
        await Promise.all(archivePromises);
        alert(`${selectedOperations.length} opération(s) archivée(s) avec succès !`);
        setSelectedOperations([]);
        setShowBatchActionsModal(false);
        fetchOperations();
      } catch (error) {
        console.error('Error archiving operations:', error);
        alert('Erreur lors de l\'archivage des opérations');
      }
    }
  };

  const handleOpportunityChange = async (e) => {
    const opportunityId = e.target.value;
    setFormData(prev => ({...prev, opportunite_id: opportunityId}));
    
    if (opportunityId) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/opportunities/${opportunityId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const opportunity = await response.json();
          setFormData(prev => ({
            ...prev,
            opportunite_id: opportunityId,
            client_name: opportunity.client_name || prev.client_name,
            description: opportunity.description || prev.description
          }));
        }
      } catch (error) {
        console.error('Error fetching opportunity:', error);
      }
    } else {
      // Reset auto-filled fields when no opportunity is selected
      setFormData(prev => ({
        ...prev,
        opportunite_id: '',
        client_name: '',
        description: ''
      }));
    }
  };

  const handleEquipmentChange = (equipment) => {
    setFormData(prev => ({
      ...prev,
      equipements_necessaires: prev.equipements_necessaires.includes(equipment)
        ? prev.equipements_necessaires.filter(eq => eq !== equipment)
        : [...prev.equipements_necessaires, equipment]
    }));
  };

  const handleMerchandiseTypeChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomMerchandiseInput('');
    } else {
      setFormData({...formData, type_marchandise: value});
    }
  };

  const handleCustomMerchandiseSubmit = async () => {
    if (!customMerchandiseInput.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/merchandise-types`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nom: customMerchandiseInput.trim(),
          description: `Type personnalisé créé le ${new Date().toLocaleDateString()}`
        })
      });
      
      if (response.ok) {
        await fetchMerchandiseTypes();
        setFormData({...formData, type_marchandise: customMerchandiseInput.trim()});
        setCustomMerchandiseInput('');
      }
    } catch (error) {
      console.error('Error creating custom merchandise type:', error);
    }
  };

  // Fonctions de tri et filtrage
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return ' ↕️';
    }
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const filteredAndSortedOperations = () => {
    return operations
      .filter(operation => {
        const matchesSearch = !searchTerm || 
          operation.numero_operation.toLowerCase().includes(searchTerm.toLowerCase()) ||
          operation.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          operation.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          operation.type_marchandise.toLowerCase().includes(searchTerm.toLowerCase()) ||
          operation.lieu_collecte.toLowerCase().includes(searchTerm.toLowerCase()) ||
          operation.lieu_livraison.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = !filterStatus || operation.statut === filterStatus;
        const matchesPriority = !filterPriority || operation.priorite === filterPriority;
        const matchesClient = !filterClient || operation.client_name.toLowerCase().includes(filterClient.toLowerCase());
        
        // Filtre pour les opportunités
        const matchesOpportunity = !filterOpportunity || 
          (filterOpportunity === 'with' && operation.opportunite_id) ||
          (filterOpportunity === 'without' && !operation.opportunite_id);
        
        return matchesSearch && matchesStatus && matchesPriority && matchesClient && matchesOpportunity;
      })
      .sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];
        
        // Gestion des dates
        if (sortField === 'date_creation' || sortField === 'date_prevue_execution' || sortField === 'date_derniere_mise_a_jour') {
          aValue = new Date(aValue || a.updated_at);
          bValue = new Date(bValue || b.updated_at);
        }
        
        // Gestion des nombres
        if (sortField === 'valeur_estimee_xof' || sortField === 'poids_kg') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }
        
        // Gestion des strings
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (sortDirection === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterStatus('');
    setFilterPriority('');
    setFilterClient('');
    setFilterOpportunity('');
    setSortField('date_creation');
    setSortDirection('desc');
  };

  // Fonctions d'affichage
  const getStatusBadge = (status) => {
    const statusColors = {
      'planifiée': 'bg-blue-100 text-blue-800',
      'en_cours': 'bg-yellow-100 text-yellow-800',
      'terminée': 'bg-green-100 text-green-800',
      'annulée': 'bg-red-100 text-red-800',
      'archivée': 'bg-gray-100 text-gray-800'
    };
    
    const statusLabels = {
      'planifiée': 'Planifiée',
      'en_cours': 'En cours',
      'terminée': 'Terminée',
      'annulée': 'Annulée',
      'archivée': 'Archivée'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityColors = {
      'Faible': 'bg-gray-100 text-gray-800',
      'Standard': 'bg-blue-100 text-blue-800',
      'Élevée': 'bg-orange-100 text-orange-800',
      'Urgent': 'bg-red-100 text-red-800',
      'Critique': 'bg-red-500 text-white'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityColors[priority] || 'bg-gray-100 text-gray-800'}`}>
        {priority}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getUniqueClients = () => {
    const clients = [...new Set(operations.map(op => op.client_name))];
    return clients.sort();
  };

  if (loading) {
    return (
      <div className="baobab-app">
        <div className="baobab-loading">
          <div className="text-center">
            <div className="baobab-spinner mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Chargement des opérations</h3>
            <p className="baobab-loading-text">Préparation de vos données logistiques...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="baobab-app">
      {/* Header modernisé */}
      <div className="baobab-header">
        <div className="baobab-header-content">
          <div className="baobab-header-main">
            {/* Titre et statistiques */}
            <div className="baobab-title-section">
              <div className="baobab-fade-in">
                <h1 className="baobab-title">Gestion des Opérations</h1>
              </div>
            </div>

            {/* Actions et contrôles - version compacte */}
            <div className="baobab-actions flex items-center gap-2">
              {/* Bouton Export compact */}
              <button
                onClick={handleExportCSV}
                disabled={exportLoading}
                className={`p-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors ${exportLoading ? 'opacity-50' : ''}`}
                title="Exporter en CSV"
              >
                {exportLoading ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
              </button>

              {/* Bouton Nouvelle Opération compact */}
              <button
                onClick={() => {
                  resetForm();
                  setShowCreateModal(true);
                }}
                className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="hidden sm:inline">Nouvelle</span>
                <span className="sm:hidden">+</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="px-4 sm:px-6 py-6">
        {/* Tab Navigation modernisée */}
        <div className="baobab-view-selector mb-6 w-fit">
          <button
            onClick={() => setActiveTab('operations')}
            className={`baobab-view-button ${activeTab === 'operations' ? 'active' : ''}`}
          >
            🏭 Opérations
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`baobab-view-button ${activeTab === 'history' ? 'active' : ''}`}
          >
            📋 Historique
          </button>
        </div>

        {/* Contenu des onglets */}
        {activeTab === 'operations' && (
        <div>
          {/* Statistiques rapides */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-2 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500">Total Opérations</h3>
          <p className="text-2xl font-bold text-green-600">{operations.length}</p>
        </div>
        <div className="bg-white p-2 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500">En cours</h3>
          <p className="text-2xl font-bold text-yellow-600">
            {operations.filter(op => op.statut === 'en_cours').length}
          </p>
        </div>
        <div className="bg-white p-2 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500">Terminées</h3>
          <p className="text-2xl font-bold text-green-600">
            {operations.filter(op => op.statut === 'terminée').length}
          </p>
        </div>
        <div className="bg-white p-2 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500">Valeur totale</h3>
          <p className="text-2xl font-bold text-purple-600">
            {formatCurrency(operations.reduce((sum, op) => sum + (op.valeur_estimee_xof || 0), 0))}
          </p>
        </div>
      </div>

      {/* Contrôles de vue et filtres */}
      <div className="bg-white p-2 rounded-lg shadow">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Boutons de vue */}
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 rounded-md text-sm font-medium ${
                viewMode === 'grid' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              📊 Cartes
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 rounded-md text-sm font-medium ${
                viewMode === 'list' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              📋 Liste
            </button>
          </div>

          {/* Barre de recherche */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Rechercher par numéro, client, description, marchandise, lieux..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Filtres */}
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">Tous les statuts</option>
              {statusOptions.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                </option>
              ))}
            </select>
            
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">Toutes priorités</option>
              {priorityOptions.map(priority => (
                <option key={priority} value={priority}>{priority}</option>
              ))}
            </select>
            
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">Tous les clients</option>
              {getUniqueClients().map(client => (
                <option key={client} value={client}>{client}</option>
              ))}
            </select>

            <select
              value={filterOpportunity}
              onChange={(e) => setFilterOpportunity(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">Toutes opportunités</option>
              <option value="with">🎯 Avec opportunité</option>
              <option value="without">❌ Sans opportunité</option>
            </select>

            <button
              onClick={resetFilters}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
            >
              Réinitialiser
            </button>
          </div>
        </div>

        {/* Compteur de résultats */}
        <div className="mt-4 text-sm text-gray-600">
          {filteredAndSortedOperations().length} opération(s) trouvée(s)
          {(searchTerm || filterStatus || filterPriority || filterClient || filterOpportunity) && (
            <span className="ml-2 text-green-600">
              (filtrée(s) sur {operations.length} au total)
            </span>
          )}
        </div>

        {/* Statistiques rapides des opportunités */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 mb-6">
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{filteredAndSortedOperations().length}</div>
            <div className="text-sm text-blue-800">Opérations trouvées</div>
          </div>
          <div className="bg-green-50 p-3 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {filteredAndSortedOperations().filter(op => op.opportunite_id).length}
            </div>
            <div className="text-sm text-green-800">🎯 Avec opportunité</div>
          </div>
          <div className="bg-red-50 p-3 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {filteredAndSortedOperations().filter(op => !op.opportunite_id).length}
            </div>
            <div className="text-sm text-red-800">❌ Sans opportunité</div>
          </div>
          <div className="bg-orange-50 p-3 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {operations.filter(op => !op.opportunite_id).length}
            </div>
            <div className="text-sm text-orange-800">❌ Total sans opportunité</div>
          </div>
        </div>
      </div>

      {/* Batch Actions Bar */}
      {selectedOperations.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">
                {selectedOperations.length} opération(s) sélectionnée(s)
              </span>
              <button
                onClick={() => setSelectedOperations([])}
                className="text-sm text-green-600 hover:text-green-800"
              >
                Désélectionner tout
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBatchArchive}
                className="px-3 py-1 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600"
              >
                📦 Archiver
              </button>
              <button
                onClick={handleBatchDelete}
                className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
              >
                🗑️ Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Affichage des opérations */}
      {viewMode === 'grid' ? (
        // Vue en grille (cartes)
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAndSortedOperations().slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((operation) => (
            <div key={operation.id} className="bg-white rounded-lg shadow-md p-3 hover:shadow-lg transition-shadow relative">
              {/* Checkbox for selection */}
              <div className="absolute top-2 left-2 z-10">
                <input
                  type="checkbox"
                  checked={selectedOperations.some(op => op.id === operation.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectOperation(operation);
                  }}
                  className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                />
              </div>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{operation.numero_operation}</h3>
                  <p className="text-sm text-gray-600">{operation.client_name}</p>
                </div>
                <div className="flex gap-2">
                  {getStatusBadge(operation.statut)}
                  {getPriorityBadge(operation.priorite)}
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                <p className="text-sm text-gray-700">{operation.description}</p>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Type: {operation.type_marchandise}</span>
                  <span>Mode: {operation.mode_transport}</span>
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Itinéraire:</span> {operation.lieu_collecte} → {operation.lieu_livraison}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Valeur:</span> {formatCurrency(operation.valeur_estimee_xof)}
                </div>
                {operation.date_prevue_execution && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Prévue le:</span> {formatDate(operation.date_prevue_execution)}
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  {/* Boutons d'action modernisés avec icônes SVG comme dans les opportunités */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewOperation(operation);
                    }}
                    className="baobab-action-button text-gray-600 hover:text-green-600"
                    title="Voir les détails"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewOperationHistory(operation);
                    }}
                    className="baobab-action-button text-gray-600 hover:text-purple-600"
                    title="Voir l'historique"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditOperation(operation);
                    }}
                    className="baobab-action-button text-gray-600 hover:text-blue-600"
                    title="Éditer"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAIRelance(operation);
                    }}
                    className="baobab-action-button text-gray-600 hover:text-indigo-600"
                    title="AI Relance"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </button>

                  {/* Menu des actions additionnelles */}
                  <div className="relative group/menu">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDropdown(activeDropdown === operation.id ? null : operation.id);
                      }}
                      className="baobab-action-button text-gray-600 hover:text-gray-900"
                      title="Plus d'actions"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                      </svg>
                    </button>
                    
                    {/* Menu dropdown */}
                    <div className={`baobab-dropdown ${activeDropdown === operation.id ? 'active' : ''}`}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicateOperation(operation);
                          setActiveDropdown(null);
                        }}
                        className="baobab-dropdown-item"
                      >
                        <span className="text-blue-500">📋</span>
                        Dupliquer
                      </button>
                      
                      {operation.statut !== 'archivée' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchiveOperation(operation);
                            setActiveDropdown(null);
                          }}
                          className="baobab-dropdown-item"
                        >
                          <span className="text-yellow-500">📦</span>
                          Archiver
                        </button>
                      )}
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteOperation(operation);
                          setActiveDropdown(null);
                        }}
                        className="baobab-dropdown-item text-red-600"
                      >
                        <span className="text-red-500">🗑️</span>
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Bouton changement de statut rapide */}
                <select
                  value={operation.statut}
                  onChange={(e) => handleStatusChange(operation, e.target.value)}
                  className="text-xs px-2 py-1 border rounded"
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Vue en liste (tableau)
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedOperations.length === filteredAndSortedOperations().length && filteredAndSortedOperations().length > 0}
                      onChange={(e) => handleSelectAllOperations(e.target.checked)}
                      className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                    />
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('numero_operation')}
                  >
                    N° Opération {getSortIcon('numero_operation')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('client_name')}
                  >
                    Client {getSortIcon('client_name')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('type_marchandise')}
                  >
                    Type {getSortIcon('type_marchandise')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('statut')}
                  >
                    Statut {getSortIcon('statut')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('priorite')}
                  >
                    Priorité {getSortIcon('priorite')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('valeur_estimee_xof')}
                  >
                    Valeur {getSortIcon('valeur_estimee_xof')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('date_creation')}
                  >
                    Date création {getSortIcon('date_creation')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('date_derniere_mise_a_jour')}
                  >
                    Dernière MAJ {getSortIcon('date_derniere_mise_a_jour')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndSortedOperations().slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((operation) => (
                  <tr key={operation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <input
                        type="checkbox"
                        checked={selectedOperations.some(op => op.id === operation.id)}
                        onChange={() => handleSelectOperation(operation)}
                        className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {operation.numero_operation}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {operation.client_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {operation.type_marchandise}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getStatusBadge(operation.statut)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getPriorityBadge(operation.priorite)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(operation.valeur_estimee_xof)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(operation.date_creation)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(operation.date_derniere_mise_a_jour || operation.updated_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewOperation(operation)}
                          className="text-green-600 hover:text-green-800 text-sm"
                        >
                          Voir
                        </button>
                        <button
                          onClick={() => handleViewOperationHistory(operation)}
                          className="text-purple-600 hover:text-purple-800 p-1 rounded hover:bg-purple-50 transition-colors"
                          title="Historique"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleAIRelance(operation)}
                          className="text-indigo-600 hover:text-indigo-800 p-1 rounded hover:bg-indigo-50 transition-colors"
                          title="AI Relance"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleEditOperation(operation)}
                          className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50 transition-colors"
                          title="Éditer"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {operation.statut !== 'archivée' && (
                          <button
                            onClick={() => handleArchiveOperation(operation)}
                            className="text-yellow-600 hover:text-yellow-800 p-1 rounded hover:bg-yellow-50 transition-colors"
                            title="Archiver"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteOperation(operation)}
                          className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                          title="Supprimer"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredAndSortedOperations().length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Aucune opération trouvée</p>
              <p className="text-gray-400 text-sm mt-2">Essayez de modifier vos filtres ou créez une nouvelle opération</p>
            </div>
          )}

          {/* Pagination */}
          {filteredAndSortedOperations().length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(filteredAndSortedOperations().length / itemsPerPage)}
              totalItems={filteredAndSortedOperations().length}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
              onItemsPerPageChange={(newItemsPerPage) => {
                setItemsPerPage(newItemsPerPage);
                setCurrentPage(1);
              }}
            />
          )}
        </div>
      )}

      {/* History Tab Content */}
      {activeTab === 'history' && (
        <div>
          <ActivityTimeline 
            documentType="operation" 
            showFilters={true}
            maxItems={50}
          />
        </div>
      )}
      </div>
      )}

      {/* Create Operation Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4" style={{zIndex: 100000}}>
          <div className="bg-white rounded-lg p-2 w-full max-w-4xl max-h-[75vh] overflow-y-auto">
            <h2 className="text-lg font-bold mb-3">Nouvelle Opération</h2>
            
            <form onSubmit={handleCreateOperation}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Première colonne */}
                <div className="space-y-1.5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      N° Opération
                    </label>
                    <input
                      type="text"
                      value={formData.numero_operation}
                      onChange={(e) => setFormData({...formData, numero_operation: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      placeholder="Laissez vide pour génération automatique"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Opportunité associée 🎯
                    </label>
                    <SearchableSelect
                      options={opportunities
                        .sort((a, b) => new Date(b.date_creation) - new Date(a.date_creation))
                        .slice(0, 20)
                        .map(opp => ({
                          id: opp.id,
                          name: `${opp.numero_opportunite} - ${opp.nom_opportunite}`,
                          numero: opp.numero_opportunite,
                          client: opp.client_name,
                          description: opp.description || '',
                          statut: opp.statut,
                          date: new Date(opp.date_creation).toLocaleDateString('fr-FR')
                        }))}
                      selectedValue={formData.opportunite_id}
                      onChange={(value) => {
                        const selectedOpp = opportunities.find(o => o.id === value);
                        if (selectedOpp) {
                          setFormData({
                            ...formData,
                            opportunite_id: value,
                            client_id: selectedOpp.client_id,
                            client_name: selectedOpp.client_name
                          });
                        } else {
                          setFormData({...formData, opportunite_id: value});
                        }
                      }}
                      placeholder="Rechercher parmi les 20 dernières opportunités..."
                      displayField="name"
                      valueField="id"
                      renderOption={(option) => (
                        <div className="flex flex-col">
                          <div className="font-semibold text-gray-900">{option.numero}</div>
                          <div className="text-sm text-gray-700">{option.name.split(' - ')[1]}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            Client: {option.client} • Statut: {option.statut} • {option.date}
                          </div>
                        </div>
                      )}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client *
                    </label>
                    <input
                      type="text"
                      value={formData.client_name}
                      onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      rows="3"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type de marchandise * 📦
                    </label>
                    <SearchableSelect
                      options={merchandiseTypes.map(type => ({ id: type.id, name: type.nom }))}
                      selectedValue={formData.type_marchandise}
                      onChange={async (value) => {
                        setFormData({...formData, type_marchandise: value});
                      }}
                      onCreateNew={async (newValue) => {
                        // Créer un nouveau type de marchandise
                        try {
                          const token = localStorage.getItem('token');
                          const response = await fetch(`${API_BASE_URL}/api/merchandise-types`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                              nom: newValue.trim(),
                              description: `Type personnalisé créé le ${new Date().toLocaleDateString()}`
                            })
                          });
                          
                          if (response.ok) {
                            await fetchMerchandiseTypes();
                            setFormData({...formData, type_marchandise: newValue.trim()});
                            return true;
                          }
                          return false;
                        } catch (error) {
                          console.error('Error creating custom merchandise type:', error);
                          return false;
                        }
                      }}
                      placeholder="Rechercher ou créer un type..."
                      displayField="name"
                      valueField="name"
                      allowCreate={true}
                      createNewLabel="+ Créer le type"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mode de transport
                    </label>
                    <select
                      value={formData.mode_transport}
                      onChange={(e) => setFormData({...formData, mode_transport: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    >
                      {transportModes.map(mode => (
                        <option key={mode} value={mode}>{mode}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priorité
                    </label>
                    <select
                      value={formData.priorite}
                      onChange={(e) => setFormData({...formData, priorite: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    >
                      {priorityOptions.map(priority => (
                        <option key={priority} value={priority}>{priority}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Deuxième colonne */}
                <div className="space-y-1.5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lieu de collecte *
                    </label>
                    <input
                      type="text"
                      value={formData.lieu_collecte}
                      onChange={(e) => setFormData({...formData, lieu_collecte: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lieu de transit
                    </label>
                    <input
                      type="text"
                      value={formData.lieu_transit}
                      onChange={(e) => setFormData({...formData, lieu_transit: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lieu de livraison *
                    </label>
                    <input
                      type="text"
                      value={formData.lieu_livraison}
                      onChange={(e) => setFormData({...formData, lieu_livraison: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Port de départ
                      </label>
                      <input
                        type="text"
                        value={formData.port_depart}
                        onChange={(e) => setFormData({...formData, port_depart: e.target.value})}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Port d'arrivée
                      </label>
                      <input
                        type="text"
                        value={formData.port_arrivee}
                        onChange={(e) => setFormData({...formData, port_arrivee: e.target.value})}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre de conteneurs
                      </label>
                      <input
                        type="number"
                        value={formData.nombre_conteneurs}
                        onChange={(e) => setFormData({...formData, nombre_conteneurs: parseInt(e.target.value) || 0})}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type de conteneur
                      </label>
                      <select
                        value={formData.type_conteneur}
                        onChange={(e) => setFormData({...formData, type_conteneur: e.target.value})}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="">Sélectionner</option>
                        {containerTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Poids (kg)
                      </label>
                      <input
                        type="number"
                        value={formData.poids_kg}
                        onChange={(e) => setFormData({...formData, poids_kg: parseFloat(e.target.value) || 0})}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                        min="0"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Volume (m³)
                      </label>
                      <input
                        type="number"
                        value={formData.volume_m3}
                        onChange={(e) => setFormData({...formData, volume_m3: parseFloat(e.target.value) || 0})}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                        min="0"
                        step="0.1"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valeur estimée (XOF)
                    </label>
                    <input
                      type="number"
                      value={formData.valeur_estimee_xof}
                      onChange={(e) => setFormData({...formData, valeur_estimee_xof: parseFloat(e.target.value) || 0})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      min="0"
                      step="1000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date prévue d'exécution
                    </label>
                    <input
                      type="date"
                      value={formData.date_prevue_execution}
                      onChange={(e) => setFormData({...formData, date_prevue_execution: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Nouveaux champs - Dates et poids */}
              <div className="space-y-1.5">
                <h3 className="text-sm font-semibold text-gray-800 mt-3 mb-2">📅 Dates de chargement/déchargement</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date chargement prévue
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.date_chargement_prevue}
                      onChange={(e) => setFormData({...formData, date_chargement_prevue: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date déchargement prévue
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.date_dechargement_prevue}
                      onChange={(e) => setFormData({...formData, date_dechargement_prevue: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Poids marchandise et véhicule */}
              <div className="space-y-1.5 mt-3">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">⚖️ Informations poids</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Poids marchandise (kg)
                    </label>
                    <input
                      type="number"
                      value={formData.poids_marchandise_kg}
                      onChange={(e) => setFormData({...formData, poids_marchandise_kg: parseFloat(e.target.value) || 0})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PTRA véhicule (T)
                    </label>
                    <input
                      type="number"
                      value={formData.ptra_vehicule_tonnes}
                      onChange={(e) => setFormData({...formData, ptra_vehicule_tonnes: parseFloat(e.target.value) || 0})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      min="0"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>

              {/* Opérateur et Chauffeur */}
              <div className="space-y-1.5 mt-3">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">👥 Assignations</h3>
                <div className="grid grid-cols-2 gap-3">
                  {/* Opérateur (Utilisateur) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Opérateur (qui pilote)
                    </label>
                    <select
                      value={formData.operateur_id}
                      onChange={(e) => {
                        const selectedUser = users.find(u => u.id === e.target.value);
                        setFormData({
                          ...formData,
                          operateur_id: e.target.value,
                          operateur_name: selectedUser ? selectedUser.full_name : ''
                        });
                      }}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">Sélectionner un opérateur</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.full_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Chauffeur (Transporteur) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Chauffeur (qui réalise)
                    </label>
                    <select
                      value={formData.chauffeur_id}
                      onChange={(e) => {
                        const selectedTransporter = transporters.find(t => t.id === e.target.value);
                        setFormData({
                          ...formData,
                          chauffeur_id: e.target.value,
                          chauffeur_name: selectedTransporter ? selectedTransporter.name : ''
                        });
                      }}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">Sélectionner un chauffeur</option>
                      {transporters.map(transporter => (
                        <option key={transporter.id} value={transporter.id}>
                          {transporter.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              {/* Types de camions (Équipements) */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Types de camions nécessaires 🚛
                </label>
                <TruckTypeSelect
                  value={formData.equipements_necessaires.join(', ')}
                  onChange={(truckType) => {
                    // Ajouter le type si pas déjà présent
                    if (!formData.equipements_necessaires.includes(truckType)) {
                      setFormData({
                        ...formData, 
                        equipements_necessaires: [...formData.equipements_necessaires, truckType]
                      });
                    }
                  }}
                  placeholder="Rechercher un type de camion..."
                />
                {/* Affichage des types sélectionnés */}
                {formData.equipements_necessaires.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.equipements_necessaires.map((equip, index) => (
                      <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                        {equip}
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              equipements_necessaires: formData.equipements_necessaires.filter((_, i) => i !== index)
                            });
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Phase 1 - Module Facturation: Checkbox pénalités d'immobilisation */}
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="facturer_penalites"
                    checked={formData.facturer_penalites_immobilisation}
                    onChange={(e) => setFormData({...formData, facturer_penalites_immobilisation: e.target.checked})}
                    className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 rounded focus:ring-amber-500"
                  />
                  <label htmlFor="facturer_penalites" className="text-sm font-medium text-amber-800">
                    🚛 Facturer les pénalités d'immobilisation en cas de retard
                  </label>
                </div>
                <p className="text-xs text-amber-600 mt-1 ml-6">
                  Si activé, les frais d'immobilisation camion pourront être facturés au client en cas d'incident ou de retard.
                </p>
              </div>

              {/* Notes */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                  rows="3"
                  placeholder="Notes supplémentaires..."
                />
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Operation Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4" style={{zIndex: 100000}}>
          <div className="bg-white rounded-lg p-2 w-full max-w-4xl max-h-[75vh] overflow-y-auto">
            <h2 className="text-lg font-bold mb-3">Modifier l'opération</h2>
            
            <form onSubmit={handleUpdateOperation}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Première colonne */}
                <div className="space-y-1.5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      N° Opération
                    </label>
                    <input
                      type="text"
                      value={formData.numero_operation}
                      onChange={(e) => setFormData({...formData, numero_operation: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      disabled
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Opportunité associée 🎯
                    </label>
                    <SearchableSelect
                      options={opportunities
                        .sort((a, b) => new Date(b.date_creation) - new Date(a.date_creation))
                        .slice(0, 20)
                        .map(opp => ({
                          id: opp.id,
                          name: `${opp.numero_opportunite} - ${opp.nom_opportunite}`,
                          numero: opp.numero_opportunite,
                          client: opp.client_name,
                          description: opp.description || '',
                          statut: opp.statut,
                          date: new Date(opp.date_creation).toLocaleDateString('fr-FR')
                        }))}
                      selectedValue={formData.opportunite_id}
                      onChange={(value) => {
                        const selectedOpp = opportunities.find(o => o.id === value);
                        if (selectedOpp) {
                          setFormData({
                            ...formData,
                            opportunite_id: value,
                            client_id: selectedOpp.client_id,
                            client_name: selectedOpp.client_name
                          });
                        } else {
                          setFormData({...formData, opportunite_id: value});
                        }
                      }}
                      placeholder="Rechercher parmi les 20 dernières opportunités..."
                      displayField="name"
                      valueField="id"
                      renderOption={(option) => (
                        <div className="flex flex-col">
                          <div className="font-semibold text-gray-900">{option.numero}</div>
                          <div className="text-sm text-gray-700">{option.name.split(' - ')[1]}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            Client: {option.client} • Statut: {option.statut} • {option.date}
                          </div>
                        </div>
                      )}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client *
                    </label>
                    <input
                      type="text"
                      value={formData.client_name}
                      onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      rows="3"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type de marchandise * 📦
                    </label>
                    <SearchableSelect
                      options={merchandiseTypes.map(type => ({ id: type.id, name: type.nom }))}
                      selectedValue={formData.type_marchandise}
                      onChange={async (value) => {
                        setFormData({...formData, type_marchandise: value});
                      }}
                      onCreateNew={async (newValue) => {
                        // Créer un nouveau type de marchandise
                        try {
                          const token = localStorage.getItem('token');
                          const response = await fetch(`${API_BASE_URL}/api/merchandise-types`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                              nom: newValue.trim(),
                              description: `Type personnalisé créé le ${new Date().toLocaleDateString()}`
                            })
                          });
                          
                          if (response.ok) {
                            await fetchMerchandiseTypes();
                            setFormData({...formData, type_marchandise: newValue.trim()});
                            return true;
                          }
                          return false;
                        } catch (error) {
                          console.error('Error creating custom merchandise type:', error);
                          return false;
                        }
                      }}
                      placeholder="Rechercher ou créer un type..."
                      displayField="name"
                      valueField="name"
                      allowCreate={true}
                      createNewLabel="+ Créer le type"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mode de transport
                    </label>
                    <select
                      value={formData.mode_transport}
                      onChange={(e) => setFormData({...formData, mode_transport: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    >
                      {transportModes.map(mode => (
                        <option key={mode} value={mode}>{mode}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Statut
                    </label>
                    <select
                      value={formData.statut}
                      onChange={(e) => setFormData({...formData, statut: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    >
                      {statusOptions.map(status => (
                        <option key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priorité
                    </label>
                    <select
                      value={formData.priorite}
                      onChange={(e) => setFormData({...formData, priorite: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    >
                      {priorityOptions.map(priority => (
                        <option key={priority} value={priority}>{priority}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Deuxième colonne */}
                <div className="space-y-1.5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lieu de collecte *
                    </label>
                    <input
                      type="text"
                      value={formData.lieu_collecte}
                      onChange={(e) => setFormData({...formData, lieu_collecte: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lieu de transit
                    </label>
                    <input
                      type="text"
                      value={formData.lieu_transit}
                      onChange={(e) => setFormData({...formData, lieu_transit: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lieu de livraison *
                    </label>
                    <input
                      type="text"
                      value={formData.lieu_livraison}
                      onChange={(e) => setFormData({...formData, lieu_livraison: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Port de départ
                      </label>
                      <input
                        type="text"
                        value={formData.port_depart}
                        onChange={(e) => setFormData({...formData, port_depart: e.target.value})}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Port d'arrivée
                      </label>
                      <input
                        type="text"
                        value={formData.port_arrivee}
                        onChange={(e) => setFormData({...formData, port_arrivee: e.target.value})}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre de conteneurs
                      </label>
                      <input
                        type="number"
                        value={formData.nombre_conteneurs}
                        onChange={(e) => setFormData({...formData, nombre_conteneurs: parseInt(e.target.value) || 0})}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type de conteneur
                      </label>
                      <select
                        value={formData.type_conteneur}
                        onChange={(e) => setFormData({...formData, type_conteneur: e.target.value})}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="">Sélectionner</option>
                        {containerTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Poids (kg)
                      </label>
                      <input
                        type="number"
                        value={formData.poids_kg}
                        onChange={(e) => setFormData({...formData, poids_kg: parseFloat(e.target.value) || 0})}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                        min="0"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Volume (m³)
                      </label>
                      <input
                        type="number"
                        value={formData.volume_m3}
                        onChange={(e) => setFormData({...formData, volume_m3: parseFloat(e.target.value) || 0})}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                        min="0"
                        step="0.1"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valeur estimée (XOF)
                    </label>
                    <input
                      type="number"
                      value={formData.valeur_estimee_xof}
                      onChange={(e) => setFormData({...formData, valeur_estimee_xof: parseFloat(e.target.value) || 0})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      min="0"
                      step="1000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date prévue d'exécution
                    </label>
                    <input
                      type="date"
                      value={formData.date_prevue_execution}
                      onChange={(e) => setFormData({...formData, date_prevue_execution: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Types de camions (Équipements) */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Types de camions nécessaires 🚛
                </label>
                <TruckTypeSelect
                  value={formData.equipements_necessaires.join(', ')}
                  onChange={(truckType) => {
                    // Ajouter le type si pas déjà présent
                    if (!formData.equipements_necessaires.includes(truckType)) {
                      setFormData({
                        ...formData, 
                        equipements_necessaires: [...formData.equipements_necessaires, truckType]
                      });
                    }
                  }}
                  placeholder="Rechercher un type de camion..."
                />
                {/* Affichage des types sélectionnés */}
                {formData.equipements_necessaires.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.equipements_necessaires.map((equip, index) => (
                      <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                        {equip}
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              equipements_necessaires: formData.equipements_necessaires.filter((_, i) => i !== index)
                            });
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                  rows="3"
                  placeholder="Notes supplémentaires..."
                />
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Mettre à jour
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedOperation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-3 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 text-red-600">Confirmer la suppression</h2>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-2">
                Êtes-vous sûr de vouloir supprimer cette opération ?
              </p>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="font-medium">{selectedOperation.numero_operation}</p>
                <p className="text-sm text-gray-600">Client: {selectedOperation.client_name}</p>
                <p className="text-sm text-gray-600">Type: {selectedOperation.type_marchandise}</p>
              </div>
              <p className="text-sm text-red-600 mt-2">
                Cette action est irréversible et supprimera définitivement l'opération.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedOperation(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmDeleteOperation}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
        </div>

      {/* View Operation Modal */}
      {showViewModal && selectedOperation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-3 w-full max-w-6xl max-h-[75vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">
                Détails de l'opération - {selectedOperation.numero_operation}
              </h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {/* Onglets */}
            <div className="flex space-x-1 mb-6 border-b border-gray-200">
              <button
                onClick={() => setActiveViewTab('details')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                  activeViewTab === 'details'
                    ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                📋 Détails de l'opération
              </button>
              <button
                onClick={() => setActiveViewTab('documents')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                  activeViewTab === 'documents'
                    ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                📄 Documents associés ({associatedDocuments.length})
              </button>
              <button
                onClick={() => setActiveViewTab('history')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                  activeViewTab === 'history'
                    ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                📜 Historique
              </button>
            </div>

            {/* Contenu des onglets */}
            {activeViewTab === 'details' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Informations générales */}
                <div className="space-y-2">
                  <div className="bg-gray-50 p-2 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Informations générales</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">N° Opération:</span>
                        <span className="font-medium">{selectedOperation.numero_operation}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Client:</span>
                        <span className="font-medium">{selectedOperation.client_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Statut:</span>
                        <span className="font-medium">{selectedOperation.statut}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Priorité:</span>
                        <span className="font-medium">{selectedOperation.priorite}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Mode transport:</span>
                        <span className="font-medium">{selectedOperation.mode_transport}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Type marchandise:</span>
                        <span className="font-medium">{selectedOperation.type_marchandise}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Opportunité:</span>
                        <span className="font-medium">
                          {selectedOperation.opportunite_id ? (
                            <span className="text-green-600">🎯 Associée</span>
                          ) : (
                            <span className="text-red-600">❌ Non associée</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Détails transport */}
                  <div className="bg-gray-50 p-2 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Détails transport</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Lieu collecte:</span>
                        <span className="font-medium">{selectedOperation.lieu_collecte}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Lieu livraison:</span>
                        <span className="font-medium">{selectedOperation.lieu_livraison}</span>
                      </div>
                      {selectedOperation.lieu_transit && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Lieu transit:</span>
                          <span className="font-medium">{selectedOperation.lieu_transit}</span>
                        </div>
                      )}
                      {selectedOperation.port_depart && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Port départ:</span>
                          <span className="font-medium">{selectedOperation.port_depart}</span>
                        </div>
                      )}
                      {selectedOperation.port_arrivee && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Port arrivée:</span>
                          <span className="font-medium">{selectedOperation.port_arrivee}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Détails cargo */}
                <div className="space-y-2">
                  <div className="bg-gray-50 p-2 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Détails cargo</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Poids (kg):</span>
                        <span className="font-medium">{selectedOperation.poids_kg?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Volume (m³):</span>
                        <span className="font-medium">{selectedOperation.volume_m3}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Valeur estimée:</span>
                        <span className="font-medium">{selectedOperation.valeur_estimee_xof?.toLocaleString()} FCFA</span>
                      </div>
                      {selectedOperation.nombre_conteneurs > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Conteneurs:</span>
                          <span className="font-medium">{selectedOperation.nombre_conteneurs} × {selectedOperation.type_conteneur}</span>
                        </div>
                      )}
                      {selectedOperation.date_prevue_execution && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date prévue:</span>
                          <span className="font-medium">{new Date(selectedOperation.date_prevue_execution).toLocaleDateString('fr-FR')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Équipements nécessaires */}
                  {selectedOperation.equipements_necessaires && selectedOperation.equipements_necessaires.length > 0 && (
                    <div className="bg-gray-50 p-2 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-3">Équipements nécessaires</h4>
                      <div className="flex flex-wrap gap-1">
                        {selectedOperation.equipements_necessaires.map((equipment, index) => (
                          <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                            {equipment}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {selectedOperation.notes && (
                    <div className="bg-gray-50 p-2 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-3">Notes</h4>
                      <p className="text-sm text-gray-700 whitespace-pre-line">{selectedOperation.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Onglet Documents associés */}
            {activeViewTab === 'documents' && (
              <div>
                {loadingDocuments ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="text-gray-500">Chargement des documents...</div>
                  </div>
                ) : associatedDocuments.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <h4 className="text-lg font-semibold text-gray-900">
                        Documents associés à cette opération ({associatedDocuments.length})
                      </h4>
                    </div>
                    
                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                      <ul className="divide-y divide-gray-200">
                        {associatedDocuments.map((document) => (
                          <li key={document.id} className="hover:bg-gray-50">
                            <div className="px-4 py-4 flex items-center justify-between">
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    document.type === 'devis' ? 'bg-blue-100 text-blue-800' :
                                    document.type === 'facture' ? 'bg-green-100 text-green-800' :
                                    'bg-orange-100 text-orange-800'
                                  }`}>
                                    {document.type === 'devis' ? '📄 Devis' :
                                     document.type === 'facture' ? '💰 Facture' :
                                     '📋 Bon de commande'}
                                  </span>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {document.reference}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    Client: {document.client?.name || 'N/A'}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    Créé le: {new Date(document.date_creation || document.date).toLocaleDateString('fr-FR')}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <div className="text-sm text-right">
                                  <div className="font-medium text-gray-900">
                                    {(document.total_ttc || 0).toLocaleString('fr-FR')} FCFA
                                  </div>
                                  <div className={`text-xs px-2 py-1 rounded ${
                                    document.statut === 'acceptée' ? 'bg-green-100 text-green-800' :
                                    document.statut === 'envoyé' ? 'bg-blue-100 text-blue-800' :
                                    document.statut === 'brouillon' ? 'bg-gray-100 text-gray-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {document.statut === 'acceptée' ? '✅ Accepté' :
                                     document.statut === 'envoyé' ? '📤 Envoyé' :
                                     document.statut === 'brouillon' ? '📝 Brouillon' :
                                     document.statut || 'N/A'}
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleViewDocument(document)}
                                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                  👁️ Voir
                                </button>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-500">
                      <div className="text-4xl mb-2">📄</div>
                      <div className="text-lg font-medium">Aucun document associé</div>
                      <div className="text-sm">Cette opération n'a pas encore de documents associés (devis, factures, bons de commande).</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Onglet Historique */}
            {activeViewTab === 'history' && (
              <div>
                <ActivityTimeline 
                  documentType="operation" 
                  documentId={selectedOperation.id}
                />
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Operation History Modal */}
      {showHistoryModal && selectedOperation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-3 w-full max-w-4xl max-h-[75vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">
                Historique - {selectedOperation.numero_operation}
              </h2>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <ActivityTimeline 
              documentType="operation" 
              documentId={selectedOperation.id}
              showFilters={true}
              maxItems={50}
            />

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Relance Modal */}
      {showAIRelanceModal && selectedOperation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-3 w-full max-w-2xl max-h-[75vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">
                🤖 Génération AI Relance - {selectedOperation.numero_operation}
              </h2>
              <button
                onClick={() => setShowAIRelanceModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <AIRelanceGenerator
              documentId={selectedOperation.id}
              documentType="operation"
              currentStatus={selectedOperation.statut || 'en_attente'}
              onRelanceGenerated={(relanceData) => {
                console.log('Relance generated for operation:', relanceData);
                // You can add additional logic here if needed
              }}
            />

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowAIRelanceModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 2 - Modal de clôture d'opération */}
      {showClotureModal && selectedOperation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-3 w-full max-w-2xl max-h-[75vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-orange-700">
                🏁 Clôture de l'opération - {selectedOperation.numero_operation}
              </h2>
              <button
                onClick={() => setShowClotureModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleClotureOperation}>
              {/* Informations obligatoires */}
              <div className="mb-6 p-2 bg-orange-50 border border-orange-200 rounded-lg">
                <h3 className="text-lg font-semibold text-orange-700 mb-4">📅 Dates et heures réelles (obligatoires)</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date de départ réelle *
                    </label>
                    <input
                      type="date"
                      value={clotureData.date_depart_reelle}
                      onChange={(e) => setClotureData({...clotureData, date_depart_reelle: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Heure de départ réelle *
                    </label>
                    <input
                      type="time"
                      value={clotureData.heure_depart_reelle}
                      onChange={(e) => setClotureData({...clotureData, heure_depart_reelle: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date d'arrivée réelle *
                    </label>
                    <input
                      type="date"
                      value={clotureData.date_arrivee_reelle}
                      onChange={(e) => setClotureData({...clotureData, date_arrivee_reelle: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Heure d'arrivée réelle *
                    </label>
                    <input
                      type="time"
                      value={clotureData.heure_arrivee_reelle}
                      onChange={(e) => setClotureData({...clotureData, heure_arrivee_reelle: e.target.value})}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Section incidents */}
              <div className="mb-6 p-2 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="text-lg font-semibold text-red-700 mb-4">Incidents rencontrés</h3>
                
                <div className="flex items-center space-x-4 mb-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="incident"
                      checked={!clotureData.has_incident}
                      onChange={() => setClotureData({...clotureData, has_incident: false, incident_types: [], incident_cr: ''})}
                      className="mr-2"
                    />
                    <span className="text-green-600 font-medium">Non - Aucun incident</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="incident"
                      checked={clotureData.has_incident}
                      onChange={() => setClotureData({...clotureData, has_incident: true})}
                      className="mr-2"
                    />
                    <span className="text-red-600 font-medium">Oui - Incident rencontré</span>
                  </label>
                </div>

                {clotureData.has_incident && (
                  <div className="space-y-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Types d'incidents (sélection multiple)
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                        {incidentTypes.map(type => (
                          <label key={type} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={clotureData.incident_types.includes(type)}
                              onChange={(e) => {
                                const newTypes = e.target.checked 
                                  ? [...clotureData.incident_types, type]
                                  : clotureData.incident_types.filter(t => t !== type);
                                setClotureData({...clotureData, incident_types: newTypes});
                              }}
                              className="mr-2"
                            />
                            <span className="text-sm">{type}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Compte-rendu de l'incident
                      </label>
                      <textarea
                        value={clotureData.incident_cr}
                        onChange={(e) => setClotureData({...clotureData, incident_cr: e.target.value})}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                        rows="4"
                        placeholder="Décrivez l'incident rencontré, les causes, les conséquences et les actions prises..."
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowClotureModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
                >
                  🏁 Clôturer l'opération
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de prévisualisation de document */}
      {showDocumentPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-2 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">
                📄 {previewDocumentTitle}
              </h2>
              <button
                onClick={() => setShowDocumentPreview(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ✕
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              <div 
                className="document-preview-content"
                dangerouslySetInnerHTML={{ __html: documentPreviewContent }} 
              />
            </div>
            
            <div className="flex justify-end p-2 border-t border-gray-200">
              <button
                onClick={() => setShowDocumentPreview(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default OperationsModule;